import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["data", "details", "svg", "viewport", "zoomValue", "selectable"]
  static values = {
    scale: { type: Number, default: 1 },
    filename: String
  }

  connect() {
    this.graph = JSON.parse(this.dataTarget.textContent)
    this.naturalWidth = Number(this.svgTarget.getAttribute("width"))
    this.naturalHeight = Number(this.svgTarget.getAttribute("height"))
    this.applyScale()
    requestAnimationFrame(() => this.center())
  }

  select(event) {
    const selectable = event.target.closest("[data-kind][data-id]")
    if (!selectable || !this.element.contains(selectable)) return

    this.activate(selectable)
  }

  selectWithKeyboard(event) {
    if (!["Enter", " "].includes(event.key)) return

    const selectable = event.target.closest("[data-kind][data-id]")
    if (!selectable) return

    event.preventDefault()
    this.activate(selectable)
  }

  zoomIn() {
    this.scaleValue = Math.min(2, this.scaleValue + 0.1)
    this.applyScale()
  }

  zoomOut() {
    this.scaleValue = Math.max(0.4, this.scaleValue - 0.1)
    this.applyScale()
  }

  center() {
    const viewport = this.viewportTarget
    viewport.scrollTo({
      left: Math.max(0, (viewport.scrollWidth - viewport.clientWidth) / 2),
      top: 0,
      behavior: "smooth"
    })
  }

  refresh() {
    window.location.reload()
  }

  exportSvg() {
    const blob = new Blob([this.serializedSvg()], {
      type: "image/svg+xml;charset=utf-8"
    })
    this.downloadBlob(blob, `${this.filenameValue || "workflow"}.svg`)
  }

  exportPng() {
    const svgBlob = new Blob([this.serializedSvg()], {
      type: "image/svg+xml;charset=utf-8"
    })
    const sourceUrl = URL.createObjectURL(svgBlob)
    const image = new Image()

    image.onload = () => {
      const ratio = Math.min(
        2,
        4096 / this.naturalWidth,
        4096 / this.naturalHeight
      )
      const canvas = document.createElement("canvas")
      canvas.width = Math.round(this.naturalWidth * ratio)
      canvas.height = Math.round(this.naturalHeight * ratio)
      const context = canvas.getContext("2d")

      context.fillStyle = "#ffffff"
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.drawImage(image, 0, 0, canvas.width, canvas.height)

      canvas.toBlob((blob) => {
        if (blob) this.downloadBlob(blob, `${this.filenameValue || "workflow"}.png`)
        URL.revokeObjectURL(sourceUrl)
      }, "image/png")
    }

    image.onerror = () => URL.revokeObjectURL(sourceUrl)
    image.src = sourceUrl
  }

  activate(element) {
    this.selectableTargets.forEach((item) => item.classList.remove("is-selected"))
    element.classList.add("is-selected")

    const id = Number(element.dataset.id)
    if (element.dataset.kind === "state") {
      this.renderState(this.graph.states.find((state) => state.id === id))
    } else {
      this.renderTransition(
        this.graph.transitions.find((transition) => transition.id === id)
      )
    }
  }

  renderState(state) {
    if (!state) return

    this.clearDetails()
    this.detailsTarget.append(
      this.heading(state.name, state.key),
      this.badges([
        state.initial ? ["Inicial", "success"] : null,
        state.final ? ["Final", "danger"] : null
      ]),
      this.definitionList([
        ["Nome", state.name],
        ["Chave", state.key],
        ["Inicial", state.initial ? "Sim" : "Não"],
        ["Final", state.final ? "Sim" : "Não"],
        ["Entradas", state.incoming_count],
        ["Saídas", state.outgoing_count]
      ])
    )

    if (state.description) {
      this.detailsTarget.append(this.section("Descrição", state.description))
    }
  }

  renderTransition(transition) {
    if (!transition) return

    const origin = this.stateById(transition.from)
    const destination = this.stateById(transition.to)
    const actions = transition.action_ids
      .map((id) => this.graph.actions.find((action) => action.id === id))
      .filter(Boolean)

    this.clearDetails()
    this.detailsTarget.append(
      this.heading(transition.event.name, transition.event.key),
      this.definitionList([
        ["Origem", origin?.name || transition.from],
        ["Evento", transition.event.name],
        ["Chave do evento", transition.event.key],
        ["Destino", destination?.name || transition.to],
        ["Ativa", transition.active ? "Sim" : "Não"],
        ["Actions", actions.length]
      ])
    )

    const title = document.createElement("h3")
    title.className = "h6 mt-4 mb-3"
    title.textContent = "Actions"
    this.detailsTarget.append(title)

    if (actions.length === 0) {
      this.detailsTarget.append(this.mutedText("Nenhuma action configurada."))
      return
    }

    actions.forEach((action) => this.detailsTarget.append(this.actionCard(action)))
  }

  actionCard(action) {
    const card = document.createElement("div")
    card.className = "workflow-graph__action-card border rounded mb-3"

    const header = document.createElement("div")
    header.className = "d-flex justify-content-between align-items-center p-3 border-bottom"
    const title = document.createElement("strong")
    title.textContent = action.type_label
    const type = document.createElement("code")
    type.textContent = action.type
    header.append(title, type)

    const tabs = document.createElement("div")
    tabs.className = "workflow-graph__action-tabs"
    const tabNavigation = document.createElement("div")
    tabNavigation.className = "nav nav-tabs px-3 pt-2"
    const summaryButton = this.tabButton("Resumo", true)
    const jsonButton = this.tabButton("JSON", false)
    tabNavigation.append(summaryButton, jsonButton)

    const panes = document.createElement("div")
    panes.className = "p-3"
    const summaryPane = document.createElement("div")
    const jsonPane = document.createElement("div")
    jsonPane.hidden = true

    const entries = Object.entries(action.configuration || {})
    summaryPane.append(
      entries.length > 0
        ? this.definitionList(entries.map(([key, value]) => [this.humanize(key), this.formatValue(value)]))
        : this.mutedText("Sem configuração.")
    )

    const pre = document.createElement("pre")
    pre.className = "workflow-graph__json mb-0"
    pre.textContent = JSON.stringify(action.configuration || {}, null, 2)
    jsonPane.append(pre)

    summaryButton.addEventListener("click", () => {
      summaryButton.classList.add("active")
      jsonButton.classList.remove("active")
      summaryPane.hidden = false
      jsonPane.hidden = true
    })
    jsonButton.addEventListener("click", () => {
      jsonButton.classList.add("active")
      summaryButton.classList.remove("active")
      jsonPane.hidden = false
      summaryPane.hidden = true
    })

    panes.append(summaryPane, jsonPane)
    tabs.append(tabNavigation, panes)
    card.append(header, tabs)
    return card
  }

  tabButton(label, active) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = `nav-link ${active ? "active" : ""}`
    button.textContent = label
    return button
  }

  applyScale() {
    this.svgTarget.style.width = `${this.naturalWidth * this.scaleValue}px`
    this.svgTarget.style.height = `${this.naturalHeight * this.scaleValue}px`
    this.zoomValueTarget.textContent = `${Math.round(this.scaleValue * 100)}%`
  }

  serializedSvg() {
    const clone = this.svgTarget.cloneNode(true)
    clone.querySelectorAll(".is-selected").forEach((item) => item.classList.remove("is-selected"))
    clone.setAttribute("width", this.naturalWidth)
    clone.setAttribute("height", this.naturalHeight)
    clone.style.removeProperty("width")
    clone.style.removeProperty("height")

    return new XMLSerializer().serializeToString(clone)
  }

  downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    document.body.append(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  stateById(id) {
    return this.graph.states.find((state) => state.id === id)
  }

  clearDetails() {
    this.detailsTarget.replaceChildren()
  }

  heading(titleText, subtitleText) {
    const wrapper = document.createElement("div")
    wrapper.className = "mb-3"
    const title = document.createElement("h2")
    title.className = "h5 mb-1"
    title.textContent = titleText
    const subtitle = document.createElement("code")
    subtitle.textContent = subtitleText
    wrapper.append(title, subtitle)
    return wrapper
  }

  badges(items) {
    const wrapper = document.createElement("div")
    wrapper.className = "d-flex gap-2 mb-3"
    items.filter(Boolean).forEach(([label, color]) => {
      const badge = document.createElement("span")
      badge.className = `badge bg-${color}`
      badge.textContent = label
      wrapper.append(badge)
    })
    return wrapper
  }

  definitionList(rows) {
    const list = document.createElement("dl")
    list.className = "row gy-2 mb-0"
    rows.forEach(([label, value]) => {
      const term = document.createElement("dt")
      term.className = "col-sm-5 text-muted"
      term.textContent = label
      const detail = document.createElement("dd")
      detail.className = "col-sm-7 mb-0 text-break"
      detail.textContent = String(value ?? "—")
      list.append(term, detail)
    })
    return list
  }

  section(titleText, bodyText) {
    const wrapper = document.createElement("div")
    wrapper.className = "mt-4"
    const title = document.createElement("h3")
    title.className = "h6"
    title.textContent = titleText
    const body = document.createElement("p")
    body.className = "mb-0"
    body.textContent = bodyText
    wrapper.append(title, body)
    return wrapper
  }

  mutedText(text) {
    const paragraph = document.createElement("p")
    paragraph.className = "text-muted mb-0"
    paragraph.textContent = text
    return paragraph
  }

  formatValue(value) {
    return typeof value === "object" && value !== null
      ? JSON.stringify(value)
      : String(value)
  }

  humanize(value) {
    const text = String(value).replaceAll("_", " ")
    return text.charAt(0).toUpperCase() + text.slice(1)
  }
}
